import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/views/Layout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue')
      },
      {
        path: 'roles',
        name: 'Roles',
        component: () => import('@/views/system/Roles.vue')
      },
      {
        path: 'roles/:id',
        name: 'RoleDetail',
        component: () => import('@/views/system/RoleDetail.vue'),
        meta: { title: '角色详情' }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/system/Users.vue')
      },
      {
        path: 'users/:id',
        name: 'UserDetail',
        component: () => import('@/views/system/UserDetail.vue'),
        meta: { title: '用户详情' }
      },
      {
        path: 'departmentManagement',
        name: 'DepartmentManagement',
        component: () => import('@/views/system/DepartmentManagement.vue')
      },
      {
        path: 'departmentManagement/:id',
        name: 'DepartmentDetail',
        component: () => import('@/views/system/DepartmentDetail.vue'),
        meta: { title: '部门详情' }
      },
      {
        path: 'organizationChart',
        name: 'OrganizationChart',
        component: () => import('@/views/system/OrganizationChart.vue')
      },
      {
        path: 'ApprovalChains',
        name: 'ApprovalChains',
        component: () => import('@/views/system/ApprovalChains.vue')
      },
      {
        path: 'ApprovalChains/:id',
        name: 'ApprovalChainDetail',
        component: () => import('@/views/system/ApprovalChainDetail.vue'),
        meta: { title: '审批链详情' }
      },
      {
        path: 'ApprovalProgress',
        name: 'ApprovalProgress',
        component: () => import('@/views/system/ApprovalProgress.vue')
      },
      {
        path: 'materials',
        name: 'Materials',
        component: () => import('@/views/metaInfo/Materials.vue')
      },
      {
        path: 'materials/:id',
        name: 'MaterialDetail',
        component: () => import('@/views/metaInfo/MaterialDetail.vue'),
        meta: { title: '物料详情' }
      },
      {
        path: 'customers',
        name: 'Customers',
        component: () => import('@/views/metaInfo/Customers.vue')
      },
      {
        path: 'customers/:id',
        name: 'CustomerDetail',
        component: () => import('@/views/metaInfo/CustomerDetail.vue'),
        meta: { title: '客户详情' }
      },
      {
        path: 'projects',
        name: 'Projects',
        component: () => import('@/views/metaInfo/Projects.vue')
      },
      {
        path: 'projects/:id',
        name: 'ProjectDetail',
        component: () => import('@/views/metaInfo/ProjectDetail.vue'),
        meta: { title: '项目详情' }
      },
      {
        path: 'suppliers',
        name: 'Suppliers',
        component: () => import('@/views/metaInfo/Suppliers.vue')
      },
      {
        path: 'suppliers/:id',
        name: 'SupplierDetail',
        component: () => import('@/views/metaInfo/SupplierDetail.vue'),
        meta: { title: '供应商详情' }
      },
      {
        path: 'quotations',
        name: 'Quotations',
        component: () => import('@/views/business/Quotations.vue')
      },
      {
        path: 'quotations/:id',
        name: 'QuotationDetail',
        component: () => import('@/views/business/QuotationDetail.vue'),
        meta: { title: '报价单详情' }
      },
      {
        path: 'pendingTasks',
        name: 'PendingTasks',
        component: () => import('@/views/approval/PendingTasks.vue'),
        meta: { title: '待处理事项' }
      },
      {
        path: 'approvalTaskDetail/:taskId',
        name: 'ApprovalTaskDetail',
        component: () => import('@/views/approval/ApprovalTaskDetail.vue'),
        meta: { title: '审批详情' }
      },
      {
        path: 'opportunitySample',
        name: 'OpportunitySample',
        component: () => import('@/views/business/OpportunitySample.vue'),
        meta: { title: '机会点送样' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 路由守卫
router.beforeEach((to, _from, next) => {
  const userStore = useUserStore()
  
  // 检查路由是否需要认证（包括父路由的 meta）
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  // 调试信息（生产环境可以移除）
  if (process.env.NODE_ENV === 'development') {
    console.log('路由守卫:', {
      path: to.path,
      name: to.name,
      requiresAuth,
      isLoggedIn: userStore.isLoggedIn,
      hasToken: !!userStore.token,
      hasUser: !!userStore.user
    })
  }
  
  if (requiresAuth && !userStore.isLoggedIn) {
    if (process.env.NODE_ENV === 'development') {
      console.log('需要认证但未登录，跳转到登录页')
    }
    next('/login')
  } else if (to.path === '/login' && userStore.isLoggedIn) {
    next('/')
  } else {
    next()
  }
})

export default router 